import React, { useEffect } from "react";
import { SidebarProps } from "../../../../views/crm-pages/conversation/types";
import { Icons } from "../../../../views/crm-pages/conversation/constant";
import { ConvoItem } from "./convoItem";

interface ExtendedSidebarProps extends SidebarProps {
  direction: "in" | "out";
  onDirectionChange: (d: "in" | "out") => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  // On the off-canvas mobile layout this pane IS the whole screen (the chat
  // pane isn't rendered alongside it) — without this it keeps its desktop
  // 260px width and leaves the rest of the screen blank.
  isMobile?: boolean;
}

export const Sidebar: React.FC<ExtendedSidebarProps> = ({
  selected,
  onSelect,
  activeTab,
  onTabChange,
  conversations,
  loading,
  onNewConversation,
  isNewConversation,
  direction,
  onDirectionChange,
  searchQuery,
  onSearchChange,
  hasMore,
  hasPrevious,
  loadingMore,
  onLoadMore,
  onLoadPrevious,
  isMobile,
}) => {
  // Force default to "Inbound" if somehow empty
  useEffect(() => {
    if (!direction || direction === "in") {
      onDirectionChange("in");
    }
  }, [direction, onDirectionChange]);

  // ── Local status filter ─────────────────────────────────────────────────────
  const getFilteredByStatus = () => {
    if (activeTab === 0) return conversations;
    if (activeTab === 1) return conversations.filter((c) => c.status === "open");
    if (activeTab === 2) return conversations.filter((c) => c.status === "pending");
    return conversations;
  };

  const displayConversations = getFilteredByStatus();

  const totalUnread = conversations.reduce(
    (sum, c) => sum + (c.unread_count || 0),
    0
  );

  // ── Direction tabs (Inbound + Outbound only) ───────────────────────────────
  const directionTabs: { label: string; value: "in" | "out" }[] = [
    { label: "Inbound", value: "in" },
    { label: "Outbound", value: "out" },
  ];

  return (
    <div className={`${isMobile ? "w-full" : "w-[260px] min-w-[200px]"} bg-white dark:bg-gray-900 border-r border-[#e8ecf4] dark:border-gray-800 flex flex-col h-full overflow-hidden`}>
      {/* Direction Tabs */}
      <div className="px-3 pt-2 pb-1 bg-[#EAEDFB] dark:bg-gray-800/60 flex gap-1">
        {directionTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => onDirectionChange(tab.value)}
            className={`flex-1 py-2 rounded-md text-[11px] font-semibold cursor-pointer transition-colors duration-150 border ${
              direction === tab.value
                ? "bg-white text-black shadow-sm dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                : "text-[#9197B3] hover:bg-slate-50 dark:text-gray-400 dark:hover:bg-gray-800 dark:border-transparent"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="p-3 pb-2 border-b border-slate-100 dark:border-gray-800 bg-[#EAEDFB] dark:bg-gray-800/60">
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-gray-800 rounded-lg p-2 border border-slate-200 dark:border-gray-700">
          <Icons.Search />
          <input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search Conversations"
            className="border-none bg-transparent outline-none text-xs text-slate-600 dark:text-gray-300 w-full"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange("")}
              className="text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300 border-none bg-transparent cursor-pointer leading-none text-base p-0"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Status Tabs */}
      <div className="p-2 px-3 flex gap-1.5 bg-[#EAEDFB] dark:bg-gray-800/60 border-b border-slate-100 dark:border-gray-800">
        <button
          onClick={() => onTabChange(0)}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer ${
            activeTab === 0
              ? "bg-indigo-500 text-white"
              : "bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-gray-400"
          }`}
        >
          All ({conversations.length}
          {totalUnread ? ` · ${totalUnread} unread` : ""})
        </button>
        <button
          onClick={() => onTabChange(1)}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer ${
            activeTab === 1
              ? "bg-indigo-500 text-white"
              : "bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-gray-400"
          }`}
        >
          Open ({conversations.filter((c) => c.status === "open").length})
        </button>
        <button
          onClick={() => onTabChange(2)}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer ${
            activeTab === 2
              ? "bg-indigo-500 text-white"
              : "bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-gray-400"
          }`}
        >
          Pending ({conversations.filter((c) => c.status === "pending").length})
        </button>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
          </div>
        ) : displayConversations.length === 0 ? (
          <div className="text-center text-slate-400 dark:text-gray-500 text-sm py-8">
            No conversations found
          </div>
        ) : (
          <>
            {displayConversations.map((c) => (
              <ConvoItem
                key={c.id}
                conv={c}
                selected={selected?.id === c.id}
                onClick={() => onSelect(c)}
              />
            ))}
            {(hasPrevious || hasMore) && (
              <div className="flex justify-center gap-4 py-3">
                {hasPrevious && (
                  <button
                    onClick={onLoadPrevious}
                    disabled={loadingMore}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer"
                  >
                    Load previous conversations
                  </button>
                )}
                {hasMore && (
                  <button
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer"
                  >
                    {loadingMore ? "Loading…" : "Load older conversations"}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* New Conversation Button */}
      <div className="p-3 border-t border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900">
        <button
          onClick={onNewConversation}
          className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-colors duration-150 ${
            isNewConversation
              ? "bg-indigo-500 text-white"
              : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-500/15 dark:text-indigo-400 dark:hover:bg-indigo-500/25"
          }`}
        >
          <Icons.Plus size={14} />
          New Conversation
        </button>
      </div>
    </div>
  );
};